/**
 * Locale-aware date / time / number formatters.
 *
 * Always uses `i18next.language` as the active locale (it's already resolved
 * against `SUPPORTED_LOCALES`, so passing it to `Intl` is safe even when the
 * detector picks a region tag like `es-AR`). Calling these in render is
 * cheap — the `Intl.*` constructors cache internally.
 */

import i18n from './i18n'

function currentLocale(): string {
  // Prefer `resolvedLanguage` (post-fallback) but tolerate the very-first
  // synchronous render before i18next finishes init.
  return i18n.resolvedLanguage || i18n.language || 'en'
}

function asDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

export function formatDate(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' },
): string {
  return asDate(value).toLocaleDateString(currentLocale(), options)
}

export function formatTime(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' },
): string {
  return asDate(value).toLocaleTimeString(currentLocale(), options)
}

export function formatDateTime(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
): string {
  return asDate(value).toLocaleString(currentLocale(), options)
}

export function formatNumber(n: number, options?: Intl.NumberFormatOptions): string {
  return n.toLocaleString(currentLocale(), options)
}

/**
 * Format an amount in the UI locale's currency style (e.g. "€24.99" in en,
 * "24,99 €" in ca). `currency` is an ISO 4217 code like "EUR".
 */
export function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat(currentLocale(), { style: 'currency', currency }).format(amount)
}

interface RelativeUnit {
  unit: Intl.RelativeTimeFormatUnit
  ms: number
}

const RELATIVE_UNITS: readonly RelativeUnit[] = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'week', ms: 7 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
]

/**
 * Format a date as relative-to-now text ("2 hours ago", "yesterday").
 * Past dates return negative values to `Intl.RelativeTimeFormat`, which
 * yields phrases like "yesterday" in supported locales.
 */
export function formatRelative(
  value: Date | string | number,
  options: Intl.RelativeTimeFormatOptions = { numeric: 'auto' },
): string {
  const date = asDate(value)
  const diffMs = date.getTime() - Date.now()
  const absDiff = Math.abs(diffMs)
  const rtf = new Intl.RelativeTimeFormat(currentLocale(), options)
  for (const { unit, ms } of RELATIVE_UNITS) {
    if (absDiff >= ms || unit === 'second') {
      const value = Math.round(diffMs / ms)
      return rtf.format(value, unit)
    }
  }
  return rtf.format(0, 'second')
}

/**
 * Format a weekday name for a given date (e.g. "Monday", "lundi").
 */
export function formatWeekday(
  value: Date | string | number,
  format: 'long' | 'short' | 'narrow' = 'long',
): string {
  return asDate(value).toLocaleDateString(currentLocale(), { weekday: format })
}
